import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';

const api = {
    get: (url, params) => apiClient.get(`/api/admin${url}`, { params }).then((r) => r.data),
    post: (url, body) => apiClient.post(`/api/admin${url}`, body).then((r) => r.data),
};
const errText = (e) => e?.response?.data?.error || e?.error || e?.message || '操作失败';
const KIND = { disk: '云盘' };
const CYCLE = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年', yearly: '年' };

/** 产品赠品（scope=product）：该套餐的订阅生效即送，随主套餐免费续；换套餐时赠品跟着换。 */
const ProductGiftRules = ({ productId }) => {
    const [rows, setRows] = useState([]);
    const [opts, setOpts] = useState({ products: [] });
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(null); // null | {gift_product_id, billing_cycle}
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/domain/gift-rules', { product_id: productId });
            if (r.success) setRows(Array.isArray(r.data) ? r.data : []);
            else message.error(r.error || '加载失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        if (!productId) return;
        load();
        api.get('/domain/gift-rules/options', { scope: 'product' }).then((r) => r.success && setOpts(r.data)).catch(() => {});
    }, [productId, load]);

    const save = async (body) => {
        setSaving(true);
        try {
            const r = await api.post('/domain/gift-rules/save', { scope: 'product', parent_product_id: productId, renewal_policy: 'free', ...body });
            if (r.success) { setDraft(null); load(); } else message.error(r.error || '保存失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        try {
            const r = await api.post('/domain/gift-rules/delete', { id: row.id });
            if (r.success) { if (r.data?.disabled) message.info('已发放过，改为停用'); load(); } else message.error(r.error || '删除失败');
        } catch (e) { message.error(errText(e)); }
    };

    const cyclesOf = (pid) => (opts.products.find((p) => p.id === pid)?.cycles || []).filter((c) => c.billing_cycle !== 'onetime');

    const columns = [
        {
            title: '赠品', key: 'p', render: (_, r) => (
                <Space size={4} wrap>
                    <Tag>{KIND[r.product_type_code] || r.product_type_code}</Tag>
                    <span>{r.product_name || `#${r.gift_product_id}`}</span>
                </Space>
            ),
        },
        { title: '周期', dataIndex: 'billing_cycle', width: 70, render: (v) => CYCLE[v] || v },
        { title: '续费', key: 'r', width: 110, render: () => <Tag color="green">随主套餐免费</Tag> },
        { title: '已发', dataIndex: 'granted', width: 60 },
        {
            title: '启用', key: 'on', width: 70, render: (_, r) => (
                <Switch size="small" checked={!!Number(r.active)} onChange={(on) => save({
                    id: r.id, gift_product_id: r.gift_product_id, billing_cycle: r.billing_cycle, active: on, note: r.note,
                })} />
            ),
        },
        {
            title: '', key: 'a', width: 70, render: (_, r) => (
                <Popconfirm title="删除该赠品？" onConfirm={() => remove(r)}><Button size="small" danger>删除</Button></Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false}
                locale={{ emptyText: '无赠品' }} scroll={{ x: 520 }} />
            {draft ? (
                <Space wrap style={{ marginTop: 8 }}>
                    <Select style={{ width: 240 }} placeholder="赠品套餐" showSearch optionFilterProp="label" value={draft.gift_product_id}
                        onChange={(v) => setDraft({ gift_product_id: v, billing_cycle: cyclesOf(v).some((c) => c.billing_cycle === 'annually') ? 'annually' : cyclesOf(v)[0]?.billing_cycle })}
                        options={opts.products.map((p) => ({ value: p.id, label: `${KIND[p.product_type_code] || p.product_type_code} · ${p.name}` }))} />
                    <Tooltip title="赠品转为付费时按此周期计价">
                        <Select style={{ width: 150 }} placeholder="周期" value={draft.billing_cycle}
                            onChange={(v) => setDraft({ ...draft, billing_cycle: v })}
                            options={cyclesOf(draft.gift_product_id).map((c) => ({ value: c.billing_cycle, label: `${CYCLE[c.billing_cycle] || c.billing_cycle} · ${c.price} ${c.currency}` }))} />
                    </Tooltip>
                    <Button type="primary" loading={saving} disabled={!draft.gift_product_id || !draft.billing_cycle} onClick={() => save(draft)}>添加</Button>
                    <Button onClick={() => setDraft(null)}>取消</Button>
                </Space>
            ) : (
                <Button size="small" icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => setDraft({})}>添加赠品</Button>
            )}
        </div>
    );
};

export default ProductGiftRules;
